// Generated types for PDMAction
// This file is auto-generated - do not edit manually

import { z } from 'zod';

import { ActionDefinitionReadAPI, ActionDefinitionWriteAPI } from '../../sysml/types/actiondefinition';
import { PDMRequirementWriteAPI } from './pdmrequirement';
import { PartDefinitionWriteAPI } from '../../sysml/types/partdefinition';
import { ActionDefinitionWriteAPISchema, ActionDefinitionReadAPISchemaObject, ActionDefinitionWriteAPISchemaObject } from '../../sysml/types/actiondefinition';
import { PDMRequirementWriteAPISchema } from './pdmrequirement';
import { PartDefinitionWriteAPISchema } from '../../sysml/types/partdefinition';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface PDMActionReadAPI extends ActionDefinitionReadAPI {
  /** Whether this action represents a physical implementation (PDM-specific concept).

        When to set True - Action maps to one of these physical entities:
        1.  **Commercial Off-The-Shelf (COTS) Assembly/Unit:**
            A unit typically purchased whole from a supplier, regardless of its internal complexity.
            Examples: motor, battery pack, pre-built Flight Controller board, GPS module
            The internal design is the supplier's responsibility.

        2.  **Design Package Block:**
            A collection of related, simpler items logically grouped for design, procurement, or inventory.
            Examples: Landing Gear Assembly, Fastener Kit, Wire Harness Assembly, Avionics Package
            The design is handled holistically by one engineering team (e.g., Mechanical, Electrical).

        3.  **Specific Component/Part:**
            A single, unique physical item with well-defined properties.
            Examples: chassis plate, specific sensor chip, propeller, PCB, custom bracket

        When to set False (default):
        - Action is purely functional/behavioral (e.g., "Navigate", "Monitor Battery")
        - Action needs further breakdown before mapping to parts
        - Action represents a process or algorithm (e.g., "Calculate Trajectory")

        Relationship with is_abstract:
        - is_abstract (from SysML): Conceptual <-> Concrete (modeling abstraction level)
        - is_implementation_block (PDM-specific): Functional <-> Physical (implementation mapping)
        - They serve different purposes and can be independently set

        Common workflows:
        - Start with functional actions (both flags = False)
        - Use AI functional breakdown to decompose abstract actions
        - Use AI part breakdown to identify implementation blocks
        - Implementation blocks become the bridge to the BOM (Bill of Materials)

        Usage in queries:
        - downstream_implementations property: Finds all implementation blocks below this action
        - split_implementations property: Separates pending functional work from physical implementations */
  is_implementation_block?: boolean;
}

export interface PDMActionWriteAPI extends ActionDefinitionWriteAPI {
  performed_by?: (string | PartDefinitionWriteAPI)[];
  sub_actions?: (string | ActionDefinitionWriteAPI)[];
  /** Whether this action represents a physical implementation (PDM-specific concept).

        When to set True - Action maps to one of these physical entities:
        1.  **Commercial Off-The-Shelf (COTS) Assembly/Unit:**
            A unit typically purchased whole from a supplier, regardless of its internal complexity.
            Examples: motor, battery pack, pre-built Flight Controller board, GPS module
            The internal design is the supplier's responsibility.

        2.  **Design Package Block:**
            A collection of related, simpler items logically grouped for design, procurement, or inventory.
            Examples: Landing Gear Assembly, Fastener Kit, Wire Harness Assembly, Avionics Package
            The design is handled holistically by one engineering team (e.g., Mechanical, Electrical).

        3.  **Specific Component/Part:**
            A single, unique physical item with well-defined properties.
            Examples: chassis plate, specific sensor chip, propeller, PCB, custom bracket

        When to set False (default):
        - Action is purely functional/behavioral (e.g., "Navigate", "Monitor Battery")
        - Action needs further breakdown before mapping to parts
        - Action represents a process or algorithm (e.g., "Calculate Trajectory")

        Relationship with is_abstract:
        - is_abstract (from SysML): Conceptual <-> Concrete (modeling abstraction level)
        - is_implementation_block (PDM-specific): Functional <-> Physical (implementation mapping)
        - They serve different purposes and can be independently set

        Common workflows:
        - Start with functional actions (both flags = False)
        - Use AI functional breakdown to decompose abstract actions
        - Use AI part breakdown to identify implementation blocks
        - Implementation blocks become the bridge to the BOM (Bill of Materials)

        Usage in queries:
        - downstream_implementations property: Finds all implementation blocks below this action
        - split_implementations property: Separates pending functional work from physical implementations */
  is_implementation_block?: boolean;
  /** Requirements assigned to this action as goals/intentions (Allocation relationship - may not yet be fulfilled) */
  requirements?: (string | PDMRequirementWriteAPI)[];
}

// ============================================================================
// Zod Schemas
// ============================================================================

export const PDMActionReadAPISchema: z.ZodType<PDMActionReadAPI> = ActionDefinitionReadAPISchemaObject.extend({
  is_implementation_block: z.boolean().optional(),
}).passthrough();

export const PDMActionWriteAPISchema: z.ZodType<PDMActionWriteAPI> = ActionDefinitionWriteAPISchemaObject.extend({
  performed_by: z.array(z.union([z.string(), z.lazy(() => PartDefinitionWriteAPISchema)])).optional(),
  sub_actions: z.array(z.union([z.string(), z.lazy(() => ActionDefinitionWriteAPISchema)])).optional(),
  is_implementation_block: z.boolean().optional(),
  requirements: z.array(z.union([z.string(), z.lazy(() => PDMRequirementWriteAPISchema)])).optional(),
}).passthrough();
